import { ReactComponent as CrownIcon } from "@assets/images/crownSvg.svg";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import BlockIcon from "@mui/icons-material/Block";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Box, Grid, Stack, Typography, CircularProgress } from "@mui/material";
import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { solidColors } from "../../../constants/segmentTools";
import { usePaletteColor } from "../../../hooks/usePaletteColor";
import { useIfUserEligible } from "../../../hooks/useIfUserEligible";
import { FEATURES } from "../../../constants/features";
import FeatureEligibility from "../../FeatureEligibility";
import { StyledTooltip } from "../../IyugaTooltip";
import EssentialPackageIcon from "@assets/images/packages/essentialPackageIcon.svg";
import {
  addCustomColorNewAction,
  deleteCustomColorAction,
  editCustomColorAction,
} from "../../../redux/actions/categoryColors";
import { getSelectedSegment } from "../../../redux/selectors/segmentBuilderSelectors";
import CustomAccordion from "../../Accordion";
import CustomColorPicker from "../../CustomColorPicker";
import ColorPalette from "../../SegmentBuilder/Shared/ColorPalette";
import ColorSwatch from "../../Swatch";
import Tabs from "../../Tabs";
import { colors } from "../../../utils";
import WheelPaletteTab from "../../SegmentBuilder/Main/WheelPaletteTab";

const SegmentColor = ({
  showTabs = true,
  title = "Segment Color",
  onColorChange = () => {},
  currentColor = "green",
  isExpanded = false,
  showSuggestedColorPalette, // This prop now correctly controls the Segment Palette tab
}) => {
  const initialInnerTabs = ["Preset", "Custom", "Wheel Palette"]; // Base tabs for inner color selection
  const categories = ["Solid", "Gradient", "Wallpaper"]; // Main categories

  const [colorsTab, setColorsTab] = useState(initialInnerTabs); // State for the inner color tabs
  const [activeColorTab, setActiveColorTab] = useState(initialInnerTabs[0]); // Initialize with the first tab
  const [expanded, setExpanded] = useState(isExpanded);
  const [selectedCategory, setSelectedCategory] = useState(categories[0]);
  const [selectedColor, setSelectedColor] = useState(currentColor);
  const [temporaryColor, setTemporaryColor] = useState("#FFFFFF");
  const [colorPickerVisible, setColorPickerVisible] = useState(false);

  const {
    isEligible: isEligibleCustomColors,
    subscription: subscriptionCustomColors,
    isCheckingEligibility: isCheckingEligibilityCustomColors,
    requiredSubscription: requiredSubscriptionCustomColors,
  } = useIfUserEligible(FEATURES.CUSTOM_HEX_COLORS);

  const {
    isEligible: isEligibleWheelPalette,
    subscription: subscriptionWheelPalette,
    isCheckingEligibility: isCheckingEligibilityWheelPalette,
    requiredSubscription: requiredSubscriptionWheelPalette,
  } = useIfUserEligible(FEATURES.ADV_PALLETE_GENERATOR);

  const selectedSegment = useSelector(getSelectedSegment);
  const { getPaletteColor } = usePaletteColor();
  const dispatch = useDispatch();

  const myColors = useSelector((state) => state.categoryColors.myCustomColors);
  const [editColorMode, setEditColorMode] = useState(false);
  const [selectedColorId, setSelectedColorId] = useState(null);

  // Effect to dynamically update the inner color tabs based on showSuggestedColorPalette
  useEffect(() => {
    // If 'showSuggestedColorPalette' is true, add 'Segment Palette' to the tabs
    // Otherwise, revert to the initial set of tabs
    const newTabs = showSuggestedColorPalette
      ? [...initialInnerTabs, "Segment Palette"]
      : initialInnerTabs;
    setColorsTab(newTabs);

    // If the currently active tab is 'Segment Palette' and it's no longer allowed,
    // default back to 'Preset' to avoid a broken UI state.
    if (!newTabs.includes(activeColorTab)) {
      setActiveColorTab("Preset");
    }
  }, [activeColorTab, showSuggestedColorPalette]); // Added showSuggestedColorPalette to dependencies

  useEffect(() => {
    setSelectedColor(currentColor);
  }, [currentColor]);

  
  const handleColorTabChange = (_, newValue) => {
    setActiveColorTab(newValue);
  };

  const handleChange = (event, isExpanded) => {
    setExpanded(isExpanded ? !expanded : false);
  };

  const handleCategoryChange = (event, newValue) => {
    setSelectedCategory(newValue);
  };

  const handleColorChange = (color) => {
    setSelectedColor(color);
    onColorChange && onColorChange(color);
  };

  const handleBlockIconClick = () => {
    setSelectedColor("transparent");
    onColorChange && onColorChange("transparent");
  };

  const handleCustomColorPicker = () => {
    setEditColorMode(false);
    setColorPickerVisible(true);
  };

  const handleTemporaryColorChange = (color) => {
    setTemporaryColor(color);
  };

  const onConfirmCustomColor = () => {
    const existingColor = myColors.find(
      (color) => color.color.color_code === temporaryColor
    );
    if (existingColor) {
      setSelectedColorId(existingColor?.id);
      handleColorChange(existingColor?.color?.color_code);
      setEditColorMode(true);
    } else {
      if (editColorMode) {
        dispatch(
          editCustomColorAction(selectedColorId, temporaryColor, (data) => {
            setSelectedColorId(data?.id);
            handleColorChange(data?.color?.color_code);
            setEditColorMode(true);
          })
        );
      } else {
        dispatch(
          addCustomColorNewAction(temporaryColor, (data) => {
            setSelectedColorId(data?.id);
            handleColorChange(data?.color?.color_code);
            setEditColorMode(true);
          })
        );
      }
    }
    setColorPickerVisible(false);
  };

  const handleColorEditClick = () => {
    setEditColorMode(true);
    setTemporaryColor(selectedColor);
    setColorPickerVisible(true);
  };

  const handleDeleteCustomColor = () => {
    dispatch(
      deleteCustomColorAction(selectedColorId, () => {
        setSelectedColorId(null); // Changed from "" to null for consistency with state init
        setSelectedColor("");
        setEditColorMode(false);
      })
    );
  };

  const renderCustomColorPalette = () => {
    return myColors.map((color) => (
      <Grid item key={color.id}>
        <Box
          onClick={() => {
            setEditColorMode(true);
            setSelectedColorId(color.id);
            handleColorChange(color.color.color_code);
          }}
        >
          <ColorSwatch
            color={color.color.color_code}
            borderColor={
              selectedColor === color.color.color_code ? "#BBBBBB" : undefined
            }
          />
        </Box>
      </Grid>
    ));
  };

  const renderColorTabs = () => {
    const categoriesWithIcons = colorsTab.map((category) => {
      const config = {
        label: category,
        value: category,
      };

      // Apply feature eligibility tooltip for "Custom" and "Wheel Palette" tabs
      if (
        (category === "Custom" && !isEligibleCustomColors) ||
        (category === "Wheel Palette" && !isEligibleWheelPalette)
      ) {
        config.customIcon = (
          <StyledTooltip
            title={"Essential Tier feature"}
            arrow
            placement="bottom"
            color={"#325AE9"}
            textColor={"#fff"}
          >
            <img
              src={EssentialPackageIcon}
              alt="info"
              style={{ width: "20px", height: "20px" }}
            />
          </StyledTooltip>
        );
      }
      return config;
    });

    return (
      <Grid item xs={12}>
        <Tabs
          categoriesWithIcons={categoriesWithIcons}
          selectedCategory={activeColorTab}
          onCategoryChange={handleColorTabChange}
          color="#81459B"
          indicatorColor="#C283DA"
        />
      </Grid>
    );
  };

  // Main rendering logic for the component content (used by both accordion and non-accordion)
  const content = (
    <Box sx={{ padding: 2, paddingTop: showTabs ? 0 : 2 }}>
      {showTabs && ( // Show main category tabs if showTabs prop is true
        <Tabs
          categories={categories}
          selectedCategory={selectedCategory}
          onCategoryChange={handleCategoryChange}
          color={"#81459B"}
          indicatorColor="#C283DA"
        />
      )}
      {selectedCategory === "Solid" && (
        <Box>
          {/* Render the inner color tabs here */}
          {renderColorTabs()}

          {activeColorTab === colorsTab[0] ? ( // Preset Tab
            <Grid container mt={1} spacing={1} alignItems="center">
              {solidColors.map((color) => (
                <Grid item key={color}>
                  <Box
                    onClick={() => {
                      handleColorChange(color);
                      setEditColorMode(false);
                      setSelectedColorId(null);
                    }}
                  >
                    <ColorSwatch
                      color={color}
                      borderColor={selectedColor === color && "#BBBBBB"}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          ) : activeColorTab === colorsTab[1] ? ( // Custom Tab
            isCheckingEligibilityCustomColors ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight={200}
              >
                <CircularProgress size={40} />
              </Box>
            ) : !isEligibleCustomColors ? (
              <FeatureEligibility
                subscription={"Essential"}
                requiredSubscription={1}
              />
            ) : (
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    mt: 2,
                    mb: 2,
                  }}
                >
                  <Stack
                    display="flex"
                    flexDirection="row"
                    justifyContent="flex-start"
                    alignItems="center"
                    mr={5}
                  >
                    <Typography
                      sx={{
                        fontWeight: "bold",
                        fontSize: "14px",
                        fontFamily: "DM Sans",
                      }}
                    >
                      Add Custom Colors
                    </Typography>
                  </Stack>
                  <Stack
                    display="flex"
                    flexDirection="row"
                    justifyContent="flex-end"
                    alignItems="center"
                  >
                    {editColorMode && (
                      <EditOutlinedIcon
                        onClick={handleColorEditClick}
                        style={{
                          fontSize: "22px",
                          cursor: "pointer",
                          marginRight: "3px",
                          color: "#625B71",
                        }}
                      />
                    )}

                    {editColorMode && (
                      <DeleteOutlineIcon
                        onClick={handleDeleteCustomColor}
                        style={{
                          fontSize: "23px",
                          cursor: "pointer",
                          marginRight: "3px",
                          color: "#625B71",
                        }}
                      />
                    )}

                    <AddCircleOutlineOutlinedIcon
                      sx={{ color: "#625B71", cursor: "pointer" }}
                      onClick={() => {
                        handleCustomColorPicker();
                      }}
                    />
                  </Stack>
                </Box>
                {!myColors.length && (
                  <Typography
                    sx={{
                      color: "#BBBBBB",
                      mt: 1,
                      fontSize: "12px",
                      fontFamily: "DM Sans",
                    }}
                  >
                    Start crafting your own unique library with an upgrade!
                  </Typography>
                )}
                <Grid container spacing={1} alignItems="center">
                  {renderCustomColorPalette()}
                </Grid>
                {colorPickerVisible && (
                  <CustomColorPicker
                    customStyle={{
                      position: "fixed",
                      left: "70.5vw",
                      bottom: "14vh",
                    }}
                    visible={colorPickerVisible}
                    onClose={() => setColorPickerVisible(false)}
                    onConfirm={onConfirmCustomColor}
                    color={temporaryColor}
                    onColorChange={handleTemporaryColorChange}
                  />
                )}
              </Box>
            )
          ) : activeColorTab === colorsTab[2] ? ( // Wheel Palette Tab
            isCheckingEligibilityWheelPalette ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight={200}
              >
                <CircularProgress size={40} />
              </Box>
            ) : !isEligibleWheelPalette ? (
              <FeatureEligibility
                subscription={"Essential"}
                requiredSubscription={1}
              />
            ) : (
              <WheelPaletteTab
                handleSelectColor={(color) =>
                  handleColorChange(`wheel_suggested_color_${color}`)
                }
                selectedColor={selectedColor}
                showButtonAndTitle={true}
              />
            )
          ) : activeColorTab === "Segment Palette" &&
            showSuggestedColorPalette ? ( // Segment Palette Tab (NEW)
            <div style={{ padding: "10px 0px" }}>
              <ColorPalette
                handleSelectColor={(color) =>
                  handleColorChange(`suggested_color_${color}`)
                }
                selectedColor={selectedColor}
              />
            </div>
          ) : null}
        </Box>
      )}
      {/* Additional categories like Gradient, Wallpaper would go here if needed */}
    </Box>
  );

  if (!isExpanded) {
    return (
      <CustomAccordion
        title={title}
        icon={
          selectedColor ? (
            <ColorSwatch
              color={getPaletteColor(selectedColor)}
              borderColor={"#79747E"}
              isAccordionIcon
            />
          ) : (
            <BlockIcon />
          )
        }
        expanded={expanded}
        onChange={handleChange}
      >
        {content} {/* Render the shared content */}
      </CustomAccordion>
    );
  }

  return (
    <>
      {content} {/* Render the shared content directly when not in accordion */}
    </>
  );
};

export default SegmentColor;